import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Table,
  TableColumn,
  Button,
  StatusBadge,
  PageHeader,
  Spinner,
  Input,
  Select,
  Badge,
} from '../../components';
import type { SelectOption } from '../../components';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  DownloadIcon,
  MixerHorizontalIcon,
  Pencil1Icon,
  EyeOpenIcon,
  LockClosedIcon,
  LockOpen1Icon,
  EnvelopeClosedIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@radix-ui/react-icons';
import {
  UserSearchStatus,
  BulkExportUsersRequest,
  UserListItem,
} from '../../types/api';
import { getUserDisplayStatus, USER_STATUS_BADGE } from './userStatusConfig';
import { userService } from '../../services/userService';
import { useUserStore } from '../../store/userStore';
import { UserMoreFiltersModal } from './components/UserMoreFiltersModal';
import {
  hasAdvancedFilters,
  INITIAL_USER_ADVANCED_FILTERS,
} from './userSearchFilters';
import './UsersList.scss';
import { useAuthStore } from '../../store/authStore';
import {
  canCreateClientProfile,
  canEditClientProfile,
  canExportClientProfiles,
} from '../../utils/rbac';

const DEBOUNCE_DELAY = 400;

type ActionTooltipProps = {
  label: string;
  children: React.ReactNode;
};

const ActionTooltip: React.FC<ActionTooltipProps> = ({ label, children }) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const showTooltip = () => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 8,
      left: rect.right,
    });
  };

  const hideTooltip = () => setPosition(null);

  return (
    <>
      <span
        ref={anchorRef}
        className="users-list__action-tooltip-wrap"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        {children}
      </span>
      {position &&
        createPortal(
          <span
            className="users-list__action-tooltip users-list__action-tooltip--fixed"
            role="tooltip"
            style={{ top: position.top, left: position.left }}
          >
            {label}
          </span>,
          document.body
        )}
    </>
  );
};

const GENDER_OPTIONS: SelectOption[] = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending Verification' },
  { value: 'invited', label: 'Invited' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'blocked', label: 'Blocked' },
];

const PAID_OPTIONS: SelectOption[] = [
  { value: 'paid', label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
];

const PAGE_SIZE_OPTIONS: SelectOption[] = [
  { value: '10', label: '10' },
  { value: '25', label: '25' },
  { value: '50', label: '50' },
];

export const UsersList: React.FC = () => {
  const navigate = useNavigate();
  const role = useAuthStore((state) => state.role);
  const canCreate = canCreateClientProfile(role);
  const canEdit = canEditClientProfile(role);
  const canExport = canExportClientProfiles(role);
  const [searchParams] = useSearchParams();
  const paidOnly = searchParams.get('paid') === 'true';
  const {
    users,
    currentPage,
    pageSize,
    totalPages,
    searchQuery,
    filters,
    isLoading,
    blockingUserId,
    invitingUserId,
    error,
    setPage,
    setPageSize,
    setFilters,
    clearFilters,
    searchUsers,
    blockUser,
    unblockUser,
    sendInviteEmail,
  } = useUserStore();

  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [exportingUsers, setExportingUsers] = useState(false);
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const isFirstMount = useRef(true);

  useEffect(() => {
    void setFilters({ paid: paidOnly ? 'paid' : '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidOnly]);

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      searchUsers(localSearch);
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [localSearch, searchUsers]);

  const hasActiveFilters =
    Boolean(filters.gender) ||
    Boolean(filters.status) ||
    Boolean(filters.paid) ||
    hasAdvancedFilters(filters.advanced) ||
    Boolean(searchQuery.trim());

  const handleViewUser = useCallback((userId: string) => {
    navigate(`/users/${userId}`);
  }, [navigate]);

  const handleEditUser = useCallback((userId: string) => {
    navigate(`/users/${userId}?edit=true`);
  }, [navigate]);

  const handleToggleBlock = useCallback(async (user: UserListItem) => {
    const isBlocked = user.isBlocked;
    const confirmed = window.confirm(
      isBlocked
        ? `Unblock ${user.name}? They will be able to access their account again.`
        : `Block ${user.name}? They will not be able to access their account.`
    );
    if (!confirmed) return;

    try {
      if (isBlocked) {
        await unblockUser(user.userId);
      } else {
        await blockUser(user.userId);
      }
    } catch (err) {
      console.error(`Failed to ${isBlocked ? 'unblock' : 'block'} user:`, err);
    }
  }, [blockUser, unblockUser]);

  const handleSendInvite = useCallback(
    async (userId: string) => {
      try {
        await sendInviteEmail(userId);
        window.alert('Invitation email sent successfully.');
      } catch (err) {
        console.error('Failed to send invitation email:', err);
      }
    },
    [sendInviteEmail]
  );

  const getPageNumbers = useCallback((): (number | string)[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages: (number | string)[] = [1];
    if (currentPage <= 4) {
      pages.push(2, 3, 4, 5, '...', totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push('...');
      for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }
    return pages;
  }, [currentPage, totalPages]);

  const emptyMessage = useMemo(() => {
    if (searchQuery.trim()) {
      return `No users found for "${searchQuery.trim()}"`;
    }
    if (hasActiveFilters) {
      return 'No users match your filters';
    }
    return 'No users found';
  }, [hasActiveFilters, searchQuery]);

  const columns: TableColumn<UserListItem>[] = useMemo(() => [
    {
      key: 'profile',
      header: 'Profile Details',
      render: (user) => (
        <div className="users-list__profile">
          <span className="users-list__profile-name">{user.name}</span>
          <span className="users-list__profile-id">{user.uniqueId || '—'}</span>
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email Address',
      render: (user) => (
        <span className="users-list__cell-text users-list__cell-text--truncate" title={user.email || undefined}>
          {user.email || '—'}
        </span>
      ),
    },
    {
      key: 'phoneNumber',
      header: 'Contact Number',
      render: (user) => (
        <span className="users-list__cell-text">{user.phoneNumber || '—'}</span>
      ),
    },
    {
      key: 'gender',
      header: 'Gender',
      render: (user) => (
        <span className="users-list__cell-text">{user.gender || '—'}</span>
      ),
    },
    {
      key: 'age',
      header: 'Age',
      width: '72px',
      render: (user) => (
        <span className="users-list__cell-text">
          {user.age != null ? user.age : '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (user) => {
        const displayStatus = getUserDisplayStatus({
          status: user.status,
          isBlocked: user.isBlocked,
        });
        const config = displayStatus ? USER_STATUS_BADGE[displayStatus] : null;
        if (!config) {
          return <span className="users-list__cell-text">{user.status || '—'}</span>;
        }
        return (
          <Badge
            variant={config.variant}
            size="small"
            className={`users-list__status${
              displayStatus === 'blocked' ? ' users-list__status--blocked' : ''
            }${displayStatus === 'inactive' ? ' users-list__status--inactive' : ''}`}
          >
            {config.label}
          </Badge>
        );
      },
    },
    {
      key: 'isPaid',
      header: 'Plan',
      width: '130px',
      render: (user) => (
        <StatusBadge
          status={user.isPaid ? 'active_plan' : 'no_active_plan'}
          size="small"
          className="users-list__plan-badge"
        />
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'center',
      width: '132px',
      render: (user) => {
        const isBlocking = blockingUserId === user.userId;
        const isSendingInvite = invitingUserId === user.userId;
        const isBlocked = user.isBlocked;
        const isInvited = user.status === 'invited' && !isBlocked;

        return (
          <div className="users-list__actions" onClick={(e) => e.stopPropagation()}>
            {canEdit && (
            <button
              type="button"
              className="users-list__action-btn"
              title="Edit profile"
              aria-label={`Edit ${user.name}`}
              onClick={() => handleEditUser(user.userId)}
            >
              <Pencil1Icon />
            </button>
            )}
            <button
              type="button"
              className="users-list__action-btn"
              title="View profile"
              aria-label={`View ${user.name}`}
              onClick={() => handleViewUser(user.userId)}
            >
              <EyeOpenIcon />
            </button>
            {canEdit && (isInvited ? (
              <ActionTooltip label="Resend mail">
                <button
                  type="button"
                  className="users-list__action-btn"
                  aria-label={`Resend mail to ${user.name}`}
                  disabled={isSendingInvite || Boolean(invitingUserId)}
                  onClick={() => void handleSendInvite(user.userId)}
                >
                  {isSendingInvite ? <Spinner size="small" /> : <EnvelopeClosedIcon />}
                </button>
              </ActionTooltip>
            ) : (
              <ActionTooltip label={isBlocked ? 'Unblock user' : 'Block user'}>
                <button
                  type="button"
                  className={`users-list__action-btn${isBlocked ? ' users-list__action-btn--blocked' : ''}`}
                  aria-label={isBlocked ? `Unblock ${user.name}` : `Block ${user.name}`}
                  disabled={isBlocking || Boolean(blockingUserId)}
                  onClick={() => void handleToggleBlock(user)}
                >
                  {isBlocking ? (
                    <Spinner size="small" />
                  ) : isBlocked ? (
                    <LockOpen1Icon />
                  ) : (
                    <LockClosedIcon />
                  )}
                </button>
              </ActionTooltip>
            ))}
          </div>
        );
      },
    },
  ], [canEdit, handleEditUser, handleViewUser, handleToggleBlock, handleSendInvite, blockingUserId, invitingUserId]);

  const handleCreateUser = useCallback(() => {
    navigate('/users/create');
  }, [navigate]);

  const handleGenderFilterChange = useCallback((value: string) => {
    void setFilters({ gender: value });
  }, [setFilters]);

  const handleStatusFilterChange = useCallback((value: string) => {
    void setFilters({ status: value });
  }, [setFilters]);

  const handlePaidFilterChange = useCallback((value: string) => {
    void setFilters({ paid: value });
  }, [setFilters]);

  const handleClearFilters = useCallback(() => {
    setLocalSearch('');
    if (paidOnly) {
      navigate('/users');
    }
    void clearFilters();
  }, [clearFilters, navigate, paidOnly]);

  const handleExport = useCallback(async () => {
    setExportingUsers(true);
    try {
      const exportStatus: UserSearchStatus | undefined =
        filters.status === 'pending'
          ? 'pending_verification'
          : filters.status
            ? (filters.status as UserSearchStatus)
            : undefined;

      const body: BulkExportUsersRequest = {
        filters: {
          ...(searchQuery.trim() ? { search: searchQuery.trim() } : {}),
          ...(filters.gender ? { gender: filters.gender } : {}),
          ...(exportStatus ? { status: exportStatus } : {}),
        },
      };

      await userService.exportAndDownload(body);
    } catch (err) {
      console.error('Failed to export users:', err);
    } finally {
      setExportingUsers(false);
    }
  }, [filters.gender, filters.status, searchQuery]);

  return (
    <div className="users-list">
      <PageHeader
        title="Client Profiles"
        subtitle={
          filters.paid === 'paid'
            ? 'Showing paid clients only.'
            : filters.paid === 'unpaid'
              ? 'Showing unpaid clients only.'
              : 'Manage and monitor all matrimony profiles across the platform.'
        }
        actions={
          (canCreate || canExport) ? (
          <div className="users-list__header-actions">
            {canExport && (
            <Button
              variant="secondary"
              className="users-list__export-btn"
              onClick={handleExport}
              disabled={exportingUsers}
            >
              {exportingUsers ? <Spinner size="small" /> : <DownloadIcon />}
              {exportingUsers ? 'Exporting…' : 'Export'}
            </Button>
            )}
            {canCreate && (
            <Button className="users-list__add-btn" onClick={handleCreateUser}>
              <PlusIcon />
              Add Profile
            </Button>
            )}
          </div>
          ) : undefined
        }
      />

      {error && (
        <div className="error-message">
          {error.message}
        </div>
      )}

      <div className="users-list__toolbar">
        <div className="users-list__toolbar-filters">
          <div className="users-list__search">
            <MagnifyingGlassIcon className="users-list__search-icon" aria-hidden="true" />
            <Input
              placeholder="Search by name, ID or email..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              className="users-list__search-input"
            />
          </div>

          <Select
            placeholder="Select Gender"
            value={filters.gender}
            onChange={handleGenderFilterChange}
            options={GENDER_OPTIONS}
            searchable={false}
            fullWidth={false}
            className="users-list__filter-select"
          />

          <Select
            placeholder="Select Status"
            value={filters.status}
            onChange={handleStatusFilterChange}
            options={STATUS_OPTIONS}
            searchable={false}
            fullWidth={false}
            className="users-list__filter-select"
          />

          <Select
            placeholder="Select Plan"
            value={filters.paid}
            onChange={handlePaidFilterChange}
            options={PAID_OPTIONS}
            searchable={false}
            fullWidth={false}
            className="users-list__filter-select"
          />

          <button
            type="button"
            className={`users-list__more-filters${
              hasAdvancedFilters(filters.advanced) ? ' users-list__more-filters--active' : ''
            }`}
            onClick={() => setIsMoreFiltersOpen(true)}
          >
            <MixerHorizontalIcon />
            More Filters
          </button>
        </div>

        <button
          type="button"
          className="users-list__clear-filters"
          onClick={handleClearFilters}
          disabled={!hasActiveFilters}
        >
          Clear All
        </button>
      </div>

      <div className="users-list__table-card">
        <Table
          className="users-list__table"
          columns={columns}
          data={users}
          isLoading={isLoading}
          emptyMessage={emptyMessage}
          onRowClick={(user) => handleViewUser(user.userId)}
        />

        {totalPages > 0 && (
          <div className="users-list__pagination">
            <div className="users-list__page-size">
              <span>Items per page:</span>
              <Select
                value={String(pageSize)}
                onChange={(value) => setPageSize(Number(value))}
                options={PAGE_SIZE_OPTIONS}
                searchable={false}
                fullWidth={false}
                className="users-list__page-size-select"
              />
            </div>

            <div className="users-list__pagination-controls">
              <button
                type="button"
                className="users-list__page-btn"
                disabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}
                aria-label="Previous page"
              >
                <ChevronLeftIcon />
              </button>

              <div className="users-list__page-numbers">
                {getPageNumbers().map((page, index) =>
                  page === '...' ? (
                    <span key={`ellipsis-${index}`} className="users-list__page-ellipsis">…</span>
                  ) : (
                    <button
                      key={`page-${page}`}
                      type="button"
                      className={`users-list__page-btn users-list__page-btn--number ${page === currentPage ? 'users-list__page-btn--active' : ''}`}
                      onClick={() => setPage(page as number)}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>

              <button
                type="button"
                className="users-list__page-btn"
                disabled={currentPage === totalPages}
                onClick={() => setPage(currentPage + 1)}
                aria-label="Next page"
              >
                <ChevronRightIcon />
              </button>
            </div>
          </div>
        )}
      </div>

      <UserMoreFiltersModal
        isOpen={isMoreFiltersOpen}
        filters={filters.advanced}
        onClose={() => setIsMoreFiltersOpen(false)}
        onApply={(advanced) => void setFilters({ advanced })}
        onClear={() => void setFilters({ advanced: INITIAL_USER_ADVANCED_FILTERS })}
      />
    </div>
  );
};
